import { Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd, Router, RouterEvent } from '@angular/router';
import {BehaviorSubject, combineLatest, Observable, ReplaySubject, Subject} from 'rxjs';
import {debounceTime, filter, map, shareReplay, startWith, takeUntil, tap} from 'rxjs/operators';
import { Location } from '../../atlas_sdk/api';
import {BusinessNavDataService, LocationCounts} from '../../data.service';
import { BusinessNavLocationTabService } from './tab/tab.service';

const l: any = document.createElement('a');

// Helper to get to safely get the parsed URL of a URL string, as browser support is lacking for `URL`
export function parseURL(href: string): any {
  l.href = href;
  return l;
}

@Injectable({providedIn: 'root'})
export class BusinessNavLocationService implements OnDestroy {
  private searchTerm$$: BehaviorSubject<string> = new BehaviorSubject('');
  public searchTerm$: Observable<string> = this.searchTerm$$.asObservable();
  private unfilteredSelectedLocationList$: Observable<Location[]>;
  public selectedLocationList$: Observable<Location[]>;
  public allListCount$: Observable<number>;
  public accountGroupListCount$: Observable<number>;
  public brandListCount$: Observable<number>;
  public loading$: Observable<boolean>;

  private _destroyed$$: Subject<void> = new Subject<void>();

  private activePath$$: BehaviorSubject<string> = new BehaviorSubject<string>(this.getTemplatedUrl(window.location.href));
  public activePath$: Observable<string> = this.activePath$$.asObservable();

  public ALL_TAB_POSITION = 0;
  public LOCATIONS_TAB_POSITION = 1;
  public BRANDS_TAB_POSITION = 2;

  constructor(
    private tabService: BusinessNavLocationTabService,
    private dataService: BusinessNavDataService,
    private router: Router
  ) {
    this.unfilteredSelectedLocationList$ = combineLatest([
      this.tabService.selectedTabIndex$,
      this.dataService.loadedBusinesses$,
      this.dataService.loadedBrands$,
      this.dataService.loadedLocations$
    ]).pipe(
      map(
        ([selectedTabIdx, businesses, brands, locations]: [
          number,
          Location[],
          Location[],
          Location[]
        ]) => {
          // Locations is 'all', businesses are 'locations', and brands are 'brands'. In what is displayed to the user...
          switch (selectedTabIdx) {
            case this.ALL_TAB_POSITION: {
              return locations;
            }
            case this.LOCATIONS_TAB_POSITION: {
              return businesses;
            }
            case this.BRANDS_TAB_POSITION: {
              return brands;
            }
            default: {
              // this shouldn't happen, default to the 'All' tab
              return locations;
            }
          }
        }
      ),
      shareReplay(1)
    );
    this.selectedLocationList$ = combineLatest([
      this.unfilteredSelectedLocationList$,
      this.searchTerm$
    ]).pipe(
      map(([locations, searchTerm]: [Location[], string]) => {
        if (searchTerm === '') {
          return locations;
        }
        return locations.filter((loc: Location) =>
          this.searchLocation(loc, searchTerm)
        );
      }),
      shareReplay(1)
    );

    this.allListCount$ = this.dataService.loadedLocations$.pipe(
      takeUntil(this._destroyed$$),
      map(location => location.length)
    );

    this.accountGroupListCount$ = this.dataService.loadedBusinesses$.pipe(
      takeUntil(this._destroyed$$),
      map(location => location.length)
    );

    this.brandListCount$ = this.dataService.loadedBrands$.pipe(
      takeUntil(this._destroyed$$),
      map(location => location.length)
    );

    this.router.events.pipe(
      takeUntil(this._destroyed$$),
      filter((e: RouterEvent) => e instanceof NavigationEnd),
      map((event: NavigationEnd) => event.url),
    ).subscribe((url: string) => {
      this.activePath$$.next(this.getTemplatedUrl(url));
    });

    this.loading$ = combineLatest(this.tabService.selectedTabIndex$, this.dataService.loadingBusinesses$, this.dataService.loadingBrands$).pipe(
      map( ([tab, businesses, brands]) => {
        switch (tab) {
          case this.ALL_TAB_POSITION: {
            return businesses || brands;
          }
          case this.LOCATIONS_TAB_POSITION: {
            return businesses;
          }
          case this.BRANDS_TAB_POSITION: {
            return brands;
          }
          default: {
            return businesses || brands;
          }
        }
      })
    );
  }

  public setSearchTerm(term: string): void {
    this.searchTerm$$.next(term);
    this.dataService.updateSearch(term);
  }

  private searchLocation(location: Location, searchTerm: string): boolean {
    if (!searchTerm) {
      return true;
    }
    searchTerm = searchTerm.toUpperCase();
    if (!!location.accountGroup) {
      // if location is account group it will have both name and address
      return (
        location.accountGroup.name.toUpperCase().indexOf(searchTerm) > -1 ||
        (location.accountGroup.address &&
          location.accountGroup.address.toUpperCase().indexOf(searchTerm) > -1)
      );
    }
    // brands don't have an address
    return location.brand.name.toUpperCase().indexOf(searchTerm) > -1;
  }

  private getTemplatedUrl(url: string): string {
    return url.split('/').map((segment) => {
      if (segment.startsWith('AG-')) {
        return '<accountGroupId>';
      } else if (segment.startsWith('G-')) {
        return '<brandPath>';
      }
      return segment;
    }).join('/');
  }

  ngOnDestroy(): void {
    this._destroyed$$.next();
    this._destroyed$$.complete();
  }
}
